import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import { Box, type BoxProps } from '../box/Box';
import { Circle, type CircleProps } from '../circle/Circle';
import styles from './Checklist.module.scss';

export interface ChecklistItemProps extends BoxProps {
  /** Props to be passed to the Circle component */
  circleProps?: Partial<CircleProps>;
  /** Content to display in the circle (sets to `circleProps.children`) */
  indicator?: React.ReactNode;
  /** Indent level, each level is 4x spacing */
  indent?: number;
  /** Whether to merge props onto the child element */
  asChild?: boolean;
  /** Test ID for component identification */
  'data-testid'?: string;
}

const ChecklistItem = React.forwardRef<HTMLDivElement, ChecklistItemProps>(
  (
    {
      circleProps,
      indicator,
      indent = 0,
      asChild = false,
      children,
      'data-testid': dataTestId = 'checklist',
      ...rest
    },
    ref
  ) => {
    // Calculate the indent spacing
    const indentSpacing = indent * (4 * 6);

    // Decide which component to render for the content wrapper
    const ContentWrapper = asChild ? Slot : Box.Column;

    return (
      <Box.Row
        className={styles.container}
        ref={ref}
        alignItems="flex-start"
        gap="medium"
        marginLeft={indentSpacing > 0 ? indentSpacing : undefined}
        data-testid={dataTestId}
        {...rest}
        flexDirection="row" // Ensure row layout is maintained even if overridden in rest
      >
        <Circle className={styles.circle} layout="list" {...circleProps}>
          {circleProps?.children || indicator}
        </Circle>
        <ContentWrapper className={styles.body} {...rest}>
          {children}
        </ContentWrapper>
      </Box.Row>
    );
  }
);

const cvaChecklist = cva(undefined, {
  variants: {
    variant: {
      default: undefined,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface ChecklistProps
  extends Omit<BoxProps, 'children'>,
    VariantProps<typeof cvaChecklist> {
  /** Children to be rendered inside the checklist */
  children?: React.ReactNode;
  /** Array of checklist items (same as using `Checklist.Item` directly) */
  items?: ChecklistItemProps[];
}
const ChecklistRoot = React.forwardRef<HTMLDivElement, ChecklistProps>(
  ({ children, items = [], variant, ...rest }, ref) => {
    const renderedChecklistItems = items.map((item, index) => {
      const circleProps: CircleProps = {
        ...item.circleProps,
        layout: 'list',
        variant: 'outline',
        children: item.indicator || index + 1,
      };
      return <ChecklistItem key={index} circleProps={circleProps} {...item} />;
    });
    return (
      <Box.Column
        ref={ref}
        gap="medium"
        data-testid="checklist-root"
        {...rest}
        className={cvaChecklist({ variant, className: rest.className })}
      >
        {!!renderedChecklistItems && renderedChecklistItems}
        {children}
      </Box.Column>
    );
  }
);

export const Checklist = Object.assign(ChecklistRoot, {
  Item: ChecklistItem,
});

Checklist.displayName = 'Checklist';
Checklist.Item.displayName = 'Checklist.Item';
